import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { PageLink, PageTitle } from '../../../../../_metronic/layout/core'
import { getListRequisites, getRequisitesTotalSum } from '../../core/_requests'
import RequisiteItem from './components/RequisiteItem/RequisiteItem'
import { Table } from '../../../../../components/Table'
import { TableHead } from '../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../components/Table/TableBody'
import { IsGranted, ROLE, useAuth } from '../../../auth'
import { REQUISITE_WRITE_ROLES } from '../Requisites/core/access'
import { DEFAULT_REQUISITE_LIST } from '../../../../../constants'
import CashBoxFilter from '../../../../common/filter/CashBoxFilter'
import ReactPaginate from 'react-paginate'
import { useLocation } from 'react-router-dom'
import { REQUISITES_BREADCRUMBS } from '../Requisites/core/consts'

const ListRequisites = ({ reservationDisabled }: { reservationDisabled: boolean }) => {
  const location = useLocation()
  const breadCrumbs = useMemo<PageLink[]>(
      () => REQUISITES_BREADCRUMBS.map((b) => ({
          ...b,
          isActive: location.pathname === b.path,
      })),
      [location.pathname]
  )
  const { currentUser } = useAuth()
  const [requisiteList, setRequisiteList] = useState(DEFAULT_REQUISITE_LIST)
  const [requisitesCount, setRequisitesCount] = useState<number>(0)
  const [errorAPI, setErrorAPI] = useState('')
  const [requisitesSum, setRequisitesSum] = useState(0)
  const [cashBoxId, setCashBoxId] = useState<number[]>([])
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [searchDebounce, setSearchDebounce] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState<number>(1)
  const [countPages, setCountPages] = useState<number>(0)

  const getRequisitesList = async () => {
    setIsLoading(true)
    let response = await getListRequisites({
      cashBoxId,
      disableReservation: reservationDisabled,
      accountNumber: searchInputRef.current?.value,
      page: page,
    })

    if (!response.data) {
      return false
    }

    if (response.data.success && Array.isArray(response.data.requisites) &&
      response.data.totalRows !== undefined && response.data.totalRows >= 0) {
      setCountPages(Math.ceil(response.data.totalRows / 99))
      setRequisitesCount(response.data.totalRows)
      setRequisiteList(response.data.requisites)
      setIsLoading(false)
      setErrorAPI('')
    } else if (response.data.errors) {
      setRequisiteList([])

      setErrorAPI('Возникла ошибка: ' + JSON.stringify(response.data.errors))
    }
  }

  const updateRequisitesSum = async () => {
    let response = await getRequisitesTotalSum({
      cashBoxId,
      accountNumber: searchInputRef.current?.value?.trim(),
      disableReservation: reservationDisabled,
      //если ищем недоступные для резервирования - то enabled не имеет значения, выборка там идёт среди всех
      //если ищем доступные для резервирования - то enabled должно быть true
      enabled: !reservationDisabled ? true : undefined,
    })

    if (!response.data) {
      return false
    }

    if (response.data.success && response.data.sum !== undefined && response.data.sum >= 0) {
      setRequisitesSum(response.data.sum)

      setErrorAPI('')
    } else if (response.data.errors) {
      setErrorAPI('Возникла ошибка: ' + JSON.stringify(response.data.errors))
    }
  }

  useEffect(() => {
    getRequisitesList()

    if (
      currentUser?.roles.some((el: any) => [ROLE.ROLE_ADMIN, ROLE.ROLE_SENIOR_CASHIER].includes(el))
    ) {
      updateRequisitesSum()
    }
  }, [cashBoxId, reservationDisabled, page])

  const requisiteItemCallback = () => {
    getRequisitesList()
    if (
      currentUser?.roles.some((el: any) => [ROLE.ROLE_ADMIN, ROLE.ROLE_SENIOR_CASHIER].includes(el))
    ) {
      updateRequisitesSum()
    }
  }

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    clearTimeout(searchDebounce)
    const newTimer = setTimeout(() => {getRequisitesList(); updateRequisitesSum();}, 500)
    setSearchDebounce(newTimer)
  }

  return (
    <div className='card'>
      <PageTitle breadcrumbs={breadCrumbs}>Реквизиты</PageTitle>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Реквизиты</span>
          <span className='text-muted mt-1 fw-semibold fs-7'>
            Количество: {requisitesCount || '-'}
          </span>
          {currentUser?.roles.some((el: any) =>
            [ROLE.ROLE_ADMIN, ROLE.ROLE_SENIOR_CASHIER].includes(el)
          ) ? (
            <span className='mt-1 fw-semibold fs-7'>Общий баланс: {requisitesSum}</span>
          ) : (
            ''
          )}
        </h3>

        <div className='col-lg-4 ms-auto me-4 align-self-center'>
          <div className='position-relative me-md-2'>
            <span className='svg-icon svg-icon-3 svg-icon-gray-500 position-absolute top-50 start-0 translate-middle ms-5'>
              <i className='fa fa-search'></i>
            </span>
            <input
              ref={searchInputRef}
              type='text'
              className='form-control form-control-solid ps-10 pt-3 pb-2'
              onChange={onSearchChange}
              placeholder='Поиск по "номер счета"'
            />
          </div>
        </div>

        {currentUser?.roles.some((el: any) =>
          [
            ROLE.ROLE_ADMIN,
            ROLE.ROLE_MANAGER,
            ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR,
            ROLE.ROLE_MERCHANT_MANAGER,
          ].includes(el)
        ) && (
          <div className='col-lg-4 ms-auto me-4 align-self-center'>
            <CashBoxFilter
              value={cashBoxId}
              changeSelect={(selected) => {
                setPage(1)
                setCashBoxId(selected.value)
              }}
            />
          </div>
        )}

        {IsGranted(REQUISITE_WRITE_ROLES, currentUser) && (
          <a
            href='/settings/requisite/add'
            className='btn btn-primary px-6 align-self-center text-nowrap'
          >
            Создать новый реквизит
          </a>
        )}
      </div>
      <div className='card-body py-3'>
        <Table>
          <TableHead>
            <tr>
              <th className='min-w-50px'>ID</th>
              <th className='min-w-50px'>Название</th>
              <th className='min-w-150px'>Тип/Платежный метод</th>
              <th className='min-w-40px'>Касса</th>
              <th className='min-w-250px'>Данные</th>
              <th className='min-w-150px'>Номер счета</th>
              <th className='min-w-120px'>Баланс</th>
              <th className='min-w-120px'>Лимит пополнений</th>
              <th className='min-w-120px'>Лимит по тикетам</th>
              <th className='min-w-80px'>Лимит депозита</th>
              <th className='min-w-80px'>Лимит по клиентам</th>
              <th className='min-w-120px text-center'>Статус</th>
              <th className='min-w-95px'>Комментарий</th>
              <th className='min-w-50px'></th>
            </tr>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <tr>
                <td colSpan={12}>Загрузка...</td>
              </tr>
            ) : (
              requisiteList.map((requisite, index) => (
                <RequisiteItem
                  requisite={requisite}
                  key={index}
                  requisiteItemCallback={requisiteItemCallback}
                  setRequisiteList={setRequisiteList}
                />
              ))
            )}
            <tr>{errorAPI ? errorAPI : <></>}</tr>
          </TableBody>
        </Table>

        <ReactPaginate
          activeClassName={'active-pagination '}
          breakClassName={'item-pagination break-me-pagination '}
          containerClassName={'pagination-pagination'}
          disabledClassName={'disabled-page-pagination'}
          nextClassName={'item-pagination ki-duotone ki-right fs-2x'}
          pageClassName={'item-pagination pagination-page-pagination '}
          previousClassName={'item-pagination ki-duotone ki-left fs-2x'}
          breakLabel='...'
          nextLabel=''
          onPageChange={(pageNumber) => {
            console.log(pageNumber.selected + 1)
            setPage(pageNumber.selected + 1)
          }}
          pageRangeDisplayed={5}
          pageCount={countPages}
          previousLabel=''
          renderOnZeroPageCount={null}
        />
      </div>
    </div>
  )
}

export default ListRequisites
